<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Header from '../../app/layout/Header.svelte';
  import { listCategories } from '../../data/categories';
  import { getBudget, setBudgetAmount } from '../../data/budgets';
  import { sumExpensesByCategoryAndMonth, sumExpensesByCategoryGroupedByDate } from '../../data/expenses';
  import { getCurrentYearMonth, getTodayDateString } from '../../lib/date';
  import { formatCurrencyJPY } from '../../lib/format';

  interface Props {
    params: { categoryId: string };
  }

  let { params }: Props = $props();

  const yearMonth = getCurrentYearMonth();
  const todayDateString = getTodayDateString();

  type CalendarCell = { date: string; day: number } | null;

  let categoryName = $state('カテゴリ');
  let budgetAmount = $state(0);
  let spentAmount = $state(0);
  let amountsByDate = $state<Record<string, number>>({});
  let loading = $state(true);
  let errorMessage = $state('');

  let editingBudget = $state(false);
  let budgetInputText = $state('');
  let savingBudget = $state(false);
  let budgetErrorMessage = $state('');

  function pad2(value: number): string {
    return String(value).padStart(2, '0');
  }

  function buildCalendarCells(ym: string): CalendarCell[] {
    const [yearStr, monthStr] = ym.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr); // 1-indexed
    const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month, 0).getDate();

    const cells: CalendarCell[] = [];
    for (let i = 0; i < firstWeekday; i++) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ date: `${ym}-${pad2(day)}`, day });
    }
    return cells;
  }

  const weekdayLabels = ['日', '月', '火', '水', '木', '金', '土'];
  const calendarCells = buildCalendarCells(yearMonth);

  const remaining = $derived(budgetAmount - spentAmount);

  async function loadData() {
    loading = true;
    errorMessage = '';
    try {
      const [categories, budget, spent, grouped] = await Promise.all([
        listCategories(),
        getBudget(params.categoryId, yearMonth),
        sumExpensesByCategoryAndMonth(params.categoryId, yearMonth),
        sumExpensesByCategoryGroupedByDate(params.categoryId, yearMonth),
      ]);
      const category = categories.find((c) => c.id === params.categoryId);
      categoryName = category?.name ?? 'カテゴリ';
      budgetAmount = budget?.amount ?? 0;
      spentAmount = spent;
      amountsByDate = grouped;
    } catch (err) {
      console.error(err);
      errorMessage = 'データの読み込みに失敗しました。';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    loadData();
  });

  function goToDay(date: string) {
    push(`/category/${params.categoryId}/day/${date}`);
  }

  function handleDayKeydown(event: KeyboardEvent, date: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goToDay(date);
    }
  }

  function goToAddExpense() {
    push(`/category/${params.categoryId}/expense/new?date=${todayDateString}`);
  }

  function startEditBudget() {
    budgetInputText = String(budgetAmount);
    budgetErrorMessage = '';
    editingBudget = true;
  }

  function cancelEditBudget() {
    editingBudget = false;
    budgetErrorMessage = '';
  }

  async function saveBudget() {
    const amount = Number(budgetInputText);
    if (!budgetInputText || Number.isNaN(amount) || !Number.isInteger(amount) || amount < 0) {
      budgetErrorMessage = '予算は0円以上の整数で入力してください。';
      return;
    }
    savingBudget = true;
    budgetErrorMessage = '';
    try {
      await setBudgetAmount(params.categoryId, yearMonth, amount);
      budgetAmount = amount;
      editingBudget = false;
    } catch (err) {
      console.error(err);
      budgetErrorMessage = '保存に失敗しました。';
    } finally {
      savingBudget = false;
    }
  }
</script>

<Header title={categoryName} showBack={true} backTo="/">
  {#snippet right()}
    <button type="button" class="add-button" onclick={goToAddExpense}>支出を追加</button>
  {/snippet}
</Header>

<main class="page">
  {#if errorMessage}
    <p class="error-text">{errorMessage}</p>
  {/if}

  <section class="summary-card">
    <div class="figure">
      <span class="figure__label">今月の予算</span>
      {#if editingBudget}
        <div class="budget-edit">
          <input
            class="budget-edit__input"
            type="number"
            inputmode="numeric"
            min="0"
            step="1"
            bind:value={budgetInputText}
            disabled={savingBudget}
          />
          <button
            type="button"
            class="budget-edit__button budget-edit__button--save"
            onclick={saveBudget}
            disabled={savingBudget}
          >
            保存
          </button>
          <button
            type="button"
            class="budget-edit__button budget-edit__button--cancel"
            onclick={cancelEditBudget}
            disabled={savingBudget}
          >
            キャンセル
          </button>
        </div>
        {#if budgetErrorMessage}
          <span class="budget-edit__error">{budgetErrorMessage}</span>
        {/if}
      {:else}
        <div class="figure__value-row">
          <span class="figure__value">{formatCurrencyJPY(budgetAmount)}</span>
          <button type="button" class="edit-button" onclick={startEditBudget}>編集</button>
        </div>
      {/if}
    </div>
    <div class="figure">
      <span class="figure__label">今月の支出合計</span>
      <span class="figure__value">{formatCurrencyJPY(spentAmount)}</span>
    </div>
    <div class="figure">
      <span class="figure__label">残りの予算</span>
      <span class="figure__value" class:figure__value--danger={remaining < 0}>
        {formatCurrencyJPY(remaining)}
      </span>
    </div>
  </section>

  {#if loading}
    <p class="muted-text">読み込み中...</p>
  {:else}
    <div class="calendar">
      <div class="calendar__weekdays">
        {#each weekdayLabels as label (label)}
          <div class="calendar__weekday">{label}</div>
        {/each}
      </div>
      <div class="calendar__grid">
        {#each calendarCells as cell, index (index)}
          {#if cell === null}
            <div class="calendar__cell calendar__cell--empty"></div>
          {:else}
            <div
              class="calendar__cell"
              class:calendar__cell--today={cell.date === todayDateString}
              role="button"
              tabindex="0"
              onclick={() => goToDay(cell.date)}
              onkeydown={(e) => handleDayKeydown(e, cell.date)}
            >
              <span class="calendar__day">{cell.day}</span>
              {#if amountsByDate[cell.date]}
                <span class="calendar__amount">{formatCurrencyJPY(amountsByDate[cell.date])}</span>
              {/if}
            </div>
          {/if}
        {/each}
      </div>
    </div>
  {/if}
</main>

<style>
  .page {
    padding: 1rem;
    max-width: 640px;
    margin: 0 auto;
  }

  .add-button {
    padding: 0.45rem 0.8rem;
    border-radius: var(--radius-md);
    border: none;
    background-color: var(--color-primary);
    color: var(--color-primary-text);
    cursor: pointer;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  .add-button:hover,
  .add-button:focus-visible {
    opacity: 0.9;
  }

  .error-text {
    color: var(--color-danger);
  }

  .muted-text {
    color: var(--color-text-muted);
  }

  .summary-card {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    box-shadow: 0 1px 2px var(--color-shadow);
  }

  .figure {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .figure__label {
    font-size: 0.72rem;
    color: var(--color-text-muted);
  }

  .figure__value {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .figure__value--danger {
    color: var(--color-danger);
  }

  .figure__value-row {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
  }

  .edit-button {
    padding: 0.15rem 0.5rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text-muted);
    font-size: 0.72rem;
    cursor: pointer;
  }

  .edit-button:hover,
  .edit-button:focus-visible {
    border-color: var(--color-primary);
    color: var(--color-primary);
    outline: none;
  }

  .budget-edit {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
  }

  .budget-edit__input {
    width: 6rem;
    padding: 0.3rem 0.45rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text);
    font-size: 0.9rem;
  }

  .budget-edit__input:focus-visible {
    outline: none;
    border-color: var(--color-primary);
  }

  .budget-edit__button {
    padding: 0.3rem 0.6rem;
    border-radius: var(--radius-sm);
    font-size: 0.78rem;
    cursor: pointer;
  }

  .budget-edit__button--save {
    border: none;
    background-color: var(--color-primary);
    color: var(--color-primary-text);
  }

  .budget-edit__button--save:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .budget-edit__button--cancel {
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text);
  }

  .budget-edit__error {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.72rem;
    color: var(--color-danger);
  }

  .calendar {
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    background-color: var(--color-bg-elevated);
  }

  .calendar__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: 1px solid var(--color-border);
  }

  .calendar__weekday {
    text-align: center;
    padding: 0.4rem 0;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    font-weight: 600;
  }

  .calendar__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  .calendar__cell {
    min-height: 3.6rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 0.25rem;
    padding: 0.35rem 0.15rem;
    border-right: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    cursor: pointer;
  }

  .calendar__cell:nth-child(7n) {
    border-right: none;
  }

  .calendar__cell--empty {
    cursor: default;
    background-color: var(--color-bg);
  }

  .calendar__cell:hover,
  .calendar__cell:focus-visible {
    background-color: var(--color-bg);
    outline: none;
  }

  .calendar__cell--today .calendar__day {
    color: var(--color-primary);
    font-weight: 700;
  }

  .calendar__day {
    font-size: 0.85rem;
    color: var(--color-text);
  }

  .calendar__amount {
    font-size: 0.7rem;
    color: var(--color-text-muted);
    text-align: center;
    word-break: break-all;
  }
</style>
